import { InterruptableTimer } from './interruptabletimer';
import { AnyHandler } from '../types/@types/sl-v-types';
import { UUID } from 'angular2-uuid';

export type HoverHandler = (target: HTMLElement, event: any, state: HoverAction) => void;

export type HoverTargetState = 'unconstructed' | 'constructed' | 'watching' | 'stopped';
export type HoverAction = 'idle' | 'cancelled' | 'hovered';

export class HoverTarget {
  private timer: InterruptableTimer;
  private hoverTarget: HTMLElement;
  private hoverAction: HoverHandler;
  public isActivated = false;
  private debounceMSecs = 2500;
  private state: HoverTargetState = 'unconstructed';
  private lastState: HoverAction = 'idle';
  public get currentState(): HoverTargetState {
    return this.state;
  }
  handlers: any = {};
  private lastPt: { x: number; y: number } | undefined;
  private uuid: string;

  constructor(debounceMSecs = 2500) {
    this.debounceMSecs = debounceMSecs;
    this.timer = new InterruptableTimer(debounceMSecs);
    this.state = 'constructed';
    this.lastState = 'idle';
  }

  private setState(newState: HoverAction, event: any) {
    if ((this.lastState !== newState) || newState === 'hovered') {
      this.lastState = newState;
      this.hoverAction?.(this.hoverTarget, event, newState);
    }
  }

  Watch(target: HTMLElement, hoverAction: HoverHandler) {
    this.uuid = this.uuid ?? UUID.UUID();
    target.setAttribute('watch-guid', this.uuid);
    this.hoverTarget = target;
    this.hoverAction = hoverAction;

    this.handlers.leave = (event: MouseEvent) => {
      if (!this.isActivated) return;
      if (this.hoverTarget.getAttribute('watch-guid') !== this.uuid) return;
      if (this.lastState !== 'cancelled') {
        if (this.lastState !== 'idle') {
          this.setState('cancelled', event);
        }
      } else {
        // After cancelling once, stay idle to avoid spam
        this.setState('idle', event);
      }
    };

    this.handlers.enter = () => {
      this.isActivated = true;
    };

    this.handlers.down = (event: MouseEvent) => {
      if (event.preventDefault) event.preventDefault();
      const testPt = { x: event.offsetX, y: event.offsetY };
      if (testPt.x === this.lastPt?.x && testPt.y === this.lastPt?.y) return false;
      this.setState('hovered', this.lastPt);
      this.lastState = 'idle';
    };

    this.handlers.move = (event: MouseEvent) => {
      if (event.preventDefault) event.preventDefault();
      this.lastPt = { x: event.offsetX, y: event.offsetY };

      if (!this.isActivated) {
        this.timer.Stop();
        this.state = 'watching';
        return;
      }

      this.timer.Start(() => {
        this.state = 'watching';
        if (!this.isActivated) return;
        this.setState('hovered', this.lastPt);

      }, this.debounceMSecs);
    };

    if (!this.hoverTarget) return;

    this.hoverTarget.addEventListener('mouseleave', this.handlers.leave);
    this.hoverTarget.addEventListener('mouseenter', this.handlers.enter);
    this.hoverTarget.addEventListener('mousedown', this.handlers.down);
    this.hoverTarget.addEventListener('mousemove', this.handlers.move);
    this.state = 'watching';
  }

  Stop() {
    if (!this.hoverTarget) return;
    this.hoverTarget.removeEventListener('mouseleave', this.handlers.leave);
    this.hoverTarget.removeEventListener('mouseenter', this.handlers.enter);
    this.hoverTarget.removeEventListener('mousedown', this.handlers.down);
    this.hoverTarget.removeEventListener('mousemove', this.handlers.move);
    this.state = 'stopped';
    this.setState('idle', {}); // reset to idle
  }

  Resume() {
    if (this.state !== 'stopped') return;
    if (!this.hoverTarget) return;
    this.hoverTarget.addEventListener('mouseleave', this.handlers.leave);
    this.hoverTarget.addEventListener('mouseenter', this.handlers.enter);
    this.hoverTarget.addEventListener('mousedown', this.handlers.down);
    this.hoverTarget.addEventListener('mousemove', this.handlers.move);
    this.state = 'watching';
    this.lastState = 'idle';
  }

  Clear() {
    this.Stop();
    this.hoverTarget = null;
    this.hoverAction = null;
    this.state = 'constructed';
  }
}
